import axios, { AxiosProgressEvent } from 'axios';

import { DocumentRequirement, DocumentUploadState } from '../domain/document-collection';
import {
  AnalyticsData,
  CollectDocumentsNextAction,
  Promotion,
  QualificationStepRequest,
  QualificationStepResponse,
} from '../domain/qualification';
import { VGS_INBOUND_URL } from '../env';
import { cleanJson, Json } from '../general/utils/form';

const nextStep = async (request: QualificationStepRequest): Promise<QualificationStepResponse> => {
  return axios
    .post<QualificationStepResponse>(`${VGS_INBOUND_URL}/qualification/next-step`, cleanJson(request as Json))
    .then((res) => res.data);
};

export type DocumentUploadProgress = Pick<DocumentRequirement, 'documentName' | 'entityToken'> & {
  progress: number;
  progressComputable: boolean;
  uploading: boolean;
};

type ProgressCallback = (event: DocumentUploadProgress) => void;

const inProgress = (
  { documentName, entityToken }: DocumentRequirement,
  event: AxiosProgressEvent,
): DocumentUploadProgress => ({
  documentName,
  entityToken,
  progress: event.total && event.lengthComputable ? Math.round((100 * event.loaded) / event.total) : 0,
  progressComputable: event.lengthComputable,
  uploading: true,
});

const completed = ({ documentName, entityToken }: DocumentRequirement): DocumentUploadProgress => ({
  documentName,
  entityToken,
  progress: 100,
  progressComputable: true,
  uploading: false,
});

const MULTIPART_HEADERS = { 'content-type': 'multipart/form-data' };

const uploadDocuments = async (
  documents: DocumentUploadState[],
  nextAction: CollectDocumentsNextAction,
  progressCallback: ProgressCallback,
): Promise<void> => {
  for (const document of documents) {
    const formData = new FormData();
    formData.append('document', document.file!);
    formData.append('document_name', document.documentName);
    formData.append('entity_token', document.entityToken);
    formData.append('next_action', nextAction);

    await axios.post('/qualification/upload_document', formData, {
      headers: MULTIPART_HEADERS,
      onUploadProgress: (event: AxiosProgressEvent) => progressCallback(inProgress(document, event)),
    });
    progressCallback(completed(document));
  }
};

const uploadFinancialDocuments = async (
  document: DocumentRequirement,
  files: File[],
  progressCallback: ProgressCallback,
): Promise<void> => {
  const formData = new FormData();
  formData.append('document_name', document.documentName);
  formData.append('entity_token', document.entityToken);
  // the service reads these back with request.files.getlist('document')
  files.forEach((file) => formData.append('document', file));

  await axios.post('/qualification/financial-documents', formData, {
    headers: MULTIPART_HEADERS,
    onUploadProgress: (event: AxiosProgressEvent) => progressCallback(inProgress(document, event)),
  });
  progressCallback(completed(document));
};

const getPromotion = async (promoCode: string): Promise<Promotion> => {
  return axios.get<Promotion>(`/qualification/active_promotion/${promoCode}`).then((res) => res.data);
};

const getAnalyticsData = (): Promise<AnalyticsData> =>
  axios.get<AnalyticsData>('/qualification/analytics').then((res) => res.data);

export { nextStep, uploadDocuments, uploadFinancialDocuments, getPromotion, getAnalyticsData };
